import { Component, OnInit } from '@angular/core';
import { DynamicDialogRef, DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { ItemDetail, ItemDetailStatusEnum, JoHeader, PoDetail, ShipBin } from 'app/model/entities/entity-model';
import { DbQueryService } from 'app/shared/db-query.service';
import { DbSaveService } from 'app/shared/db-save.service';
import { DialogWrapperService } from 'app/shared/dialog-wrapper.service';
import { ShipBinService } from 'app/shared/ship-bin.service';
import _ from 'lodash';

export interface ShipBinCheckoutDialogParams {
  shipBin: ShipBin;
}

export interface JoCheckoutStatus {
  joHeaderId: number;
  joh: JoHeader;
  accountName: string;
  employeeName: string;
  joDate: Date;
  expectedShipDate: Date;
  remainingToShip: number;
  binItemCount: number;
  /** Items in the bin that are damaged, missing or unknown. These block release. */
  nonShippableCount: number;
  openAfterRelease: number;
  shipPrepCount: number;
  inProcessCount: number;
  backorderQty: number;
  inProcessItems: ItemDetail[];
  backorderItems: PoDetail[];
  nextExpectedReceiveDate: Date | null;
  /** 'good' = green check, 'warn' = orange ?, 'block' = red X */
  status: 'good' | 'warn' | 'block';
  isCollapsed: boolean;
}

@Component({
  selector: 'app-ship-bin-checkout-dialog',
  templateUrl: './ship-bin-checkout-dialog.component.html',
  standalone: false,
})
export class ShipBinCheckoutDialogComponent implements OnInit {
  isPageReady = false;
  isReleasing = false;
  confirmDialogKey = 'ship-bin-checkout-confirm';

  shipBin!: ShipBin;
  binItems: ItemDetail[] = [];
  joStatuses: JoCheckoutStatus[] = [];

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig<ShipBinCheckoutDialogParams>,
    private dbQueryService: DbQueryService,
    private dbSaveService: DbSaveService,
    private shipBinService: ShipBinService,
    private dialogService: DialogWrapperService,
    private toastr: ToastrService,
  ) {}

  public static async openDialog(dialogService: DialogService, data: ShipBinCheckoutDialogParams) {
    const ref = dialogService.open(ShipBinCheckoutDialogComponent, {
      header: 'Bin Check Out and Release',
      closable: true,
      draggable: true,
      modal: true,
      width: '50vw',
      height: '75vh',
      focusOnShow: false,
      data: data,
    });
    return await firstValueFrom(ref.onClose);
  }

  async ngOnInit() {
    this.shipBin = this.config.data!.shipBin;
    await this.loadCheckoutStatus();
    this.isPageReady = true;
  }

  async loadCheckoutStatus() {
    this.binItems = await this.dbQueryService.getShipBinItemDetails(this.shipBin.id);
    const joHeaderIds = _.uniq(this.binItems.map((itd) => itd.joDetail?.joHeader?.id).filter(Boolean));

    if (joHeaderIds.length === 0) {
      this.joStatuses = [];
      return;
    }

    const results = await Promise.all(
      joHeaderIds.map(async (joHeaderId) => {
        const [inProcess, backorders] = await Promise.all([
          this.dbQueryService.getInProcessItemDetailsByJoHeaderId(joHeaderId!),
          this.dbQueryService.getBackorderPoDetailsByJoHeaderId(joHeaderId!),
        ]);

        const joh = this.binItems.find((itd) => itd.joDetail?.joHeader?.id === joHeaderId)!.joDetail!.joHeader!;
        const accountName = joh.account?.accountName ?? '';
        const employeeName = joh.getFormalEmployeeName() ?? '';
        const joDate = joh.joDate!;
        const expectedShipDate = joh.origExpectedShipDate!;

        const binItemsForJo = this.binItems.filter((itd) => itd.joDetail?.joHeader?.id === joHeaderId);
        const binItemCount = binItemsForJo.length;
        const nonShippableCount = binItemsForJo.filter((itd) => !itd.isShippableGoods).length;
        const shipPrepCount = inProcess.filter((itd) => itd.itemDetailStatusId === ItemDetailStatusEnum.InProcessPendingShipPrep).length;
        const inProcessCount = inProcess.length - shipPrepCount;
        const backorderQty = _.sumBy(backorders, (pod) => pod.getRemainingQty());
        const remainingToShip = binItemCount + inProcess.length + backorderQty;
        const openAfterRelease = shipPrepCount + inProcessCount + backorderQty;

        const receiveDates = backorders
          .map((pod) => pod.expectedReceiveDate)
          .filter((d): d is Date => !!d)
          .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
        const nextExpectedReceiveDate = receiveDates.length > 0 ? receiveDates[0] : null;

        const pendingShipPrep = inProcess.filter((itd) => itd.itemDetailStatusId === ItemDetailStatusEnum.InProcessPendingShipPrep);
        const inAddon = inProcess.filter(
          (itd) =>
            itd.itemDetailStatusId === ItemDetailStatusEnum.InProcessPendingAddon ||
            itd.itemDetailStatusId === ItemDetailStatusEnum.InProcessAddon,
        );

        let status: JoCheckoutStatus['status'] = 'good';
        if (pendingShipPrep.length > 0 || nonShippableCount > 0) {
          status = 'block';
        } else if (inAddon.length > 0 || backorders.length > 0) {
          status = 'warn';
        }

        return {
          joHeaderId: joHeaderId!,
          joh,
          accountName,
          employeeName,
          joDate,
          expectedShipDate,
          remainingToShip,
          binItemCount,
          nonShippableCount,
          openAfterRelease,
          shipPrepCount,
          inProcessCount,
          backorderQty,
          inProcessItems: inProcess,
          backorderItems: backorders,
          nextExpectedReceiveDate,
          status,
          isCollapsed: true,
        } as JoCheckoutStatus;
      }),
    );

    this.joStatuses = _.orderBy(results, [(r) => ({ block: 0, warn: 1, good: 2 })[r.status]]);
  }

  get goodCount() {
    return this.joStatuses.filter((s) => s.status === 'good').length;
  }

  get warnCount() {
    return this.joStatuses.filter((s) => s.status === 'warn').length;
  }

  get blockCount() {
    return this.joStatuses.filter((s) => s.status === 'block').length;
  }

  get totalRemaining() {
    return _.sumBy(this.joStatuses, (jo) => jo.remainingToShip);
  }

  get totalNonShippable() {
    return _.sum(this.joStatuses.map((jo) => jo.nonShippableCount));
  }

  get totalInBin() {
    return _.sumBy(this.joStatuses, (jo) => jo.binItemCount);
  }

  get totalOpenAfterRelease() {
    return _.sumBy(this.joStatuses, (jo) => jo.openAfterRelease);
  }

  get totalShipPrep() {
    return _.sumBy(this.joStatuses, (jo) => jo.shipPrepCount);
  }

  get totalInProcess() {
    return _.sumBy(this.joStatuses, (jo) => jo.inProcessCount);
  }

  get totalBackorder() {
    return _.sumBy(this.joStatuses, (jo) => jo.backorderQty);
  }

  get canRelease(): boolean {
    return this.joStatuses.length > 0 && this.joStatuses.every((jo) => jo.status !== 'block');
  }

  get releaseBlockedReason(): string {
    if (this.canRelease) return '';
    if (this.joStatuses.length === 0) {
      return 'You cannot release the bin. The bin has no items.';
    }
    if (this.totalNonShippable > 0) {
      return 'You cannot release the bin. Remove the damaged and missing items from the bin.';
    }
    return 'You cannot release the bin. Scan in the items that wait for Ship Prep.';
  }

  get totalAddonCount(): number {
    return _.sum(
      this.joStatuses.map(
        (jo) =>
          jo.inProcessItems.filter(
            (itd) =>
              itd.itemDetailStatusId === ItemDetailStatusEnum.InProcessPendingAddon ||
              itd.itemDetailStatusId === ItemDetailStatusEnum.InProcessAddon,
          ).length,
      ),
    );
  }

  getStatusIcon(status: JoCheckoutStatus['status']): string {
    switch (status) {
      case 'good':
        return 'pi pi-check-circle';
      case 'warn':
        return 'pi pi-question-circle';
      case 'block':
        return 'pi pi-times-circle';
    }
  }

  getStatusColor(status: JoCheckoutStatus['status']): string {
    switch (status) {
      case 'good':
        return 'var(--p-green-500, #22c55e)';
      case 'warn':
        return 'var(--p-orange-500, #f97316)';
      case 'block':
        return 'var(--p-red-500, #ef4444)';
    }
  }

  getStatusLabel(jo: JoCheckoutStatus): string {
    if (jo.nonShippableCount > 0) {
      return 'Damaged Items In Bin';
    }
    switch (jo.status) {
      case 'good':
        return 'Ready to Ship';
      case 'warn':
        return 'Has Backorders';
      case 'block':
        return 'Items In Process';
    }
  }

  async releaseForShipping() {
    // TODO(cleanup): both early-exit guards below have code smells; leave in place
    // until the phantom-voucher investigation closes, then revisit as one PR.
    //   * isReleasing: ad-hoc reentry mutex duplicated across the codebase
    //     (see isPreparingInvoice, isCheckoutOpening, etc). Convert to
    //     [disabled]="isReleasing" on the button so the DOM enforces it, and
    //     sweep the other hand-rolled flags at the same time.
    //   * !shipBin.isOpen: TOCTOU on stale Breeze cache; provides no user
    //     feedback on silent no-op. Server-side Rule 2 in
    //     UniformaxPersistenceManager already enforces the real invariant, so
    //     this client-side check is redundant for correctness. Delete it and
    //     let Rule 2's friendly toast handle the "bin already released" case.
    if (this.isReleasing) return;
    if (!this.shipBin.isOpen) return;

    this.isReleasing = true;
    try {
      let message = `Release Bin ${this.shipBin.id} (${this.binItems.length} items across ${this.joStatuses.length} job orders)?`;
      if (this.totalAddonCount > 0) {
        message = `${this.totalAddonCount} item(s) are still in add-on processing.\n\n${message}`;
      }

      const confirmed = await this.dialogService.yesNoDialog('Continue with Release', message);
      if (!confirmed) return;

      for (const jo of this.joStatuses) {
        const itemsForJo = this.binItems.filter((itd) => itd.joDetail?.joHeader?.id === jo.joHeaderId);
        await this.shipBinService.initializeInvoice(jo.joh, itemsForJo, this.shipBin);
      }
      this.shipBin.isOpen = false;
      const ok = await this.dbSaveService.saveChanges();
      if (ok) {
        this.toastr.success(`Bin ${this.shipBin.id} released — vouchers created successfully.`);
        this.ref.close(true);
      }
    } finally {
      this.isReleasing = false;
    }
  }

  onClose() {
    this.ref.close(null);
  }
}
